import { useEffect, useState } from "react";
import { searchDriveItems, type GraphSearchItem } from "../../email/services/graph";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";

interface UseKnowledgeSearchResult {
  results: GraphSearchItem[];
  loading: boolean;
  error: string | null;
}

/** Ignore 0–1 character queries — too broad, and one keystroke shouldn't fire a
 *  tenant-wide search. Below this the hook is idle and the module browses. */
const MIN_QUERY_LENGTH = 2;
/** Debounce so typing doesn't fire a request per keystroke. */
const DEBOUNCE_MS = 300;

/**
 * Tenant-wide Knowledge Base search. When the (trimmed) query is long enough,
 * debounces and calls the Microsoft Search API via `searchDriveItems`, matching
 * documents across every SharePoint site the user can access. Idle (empty
 * results, no request) for a short/empty query or missing token. Cancels
 * superseded keystrokes and stale responses.
 *
 * `includeContent` widens the match from name/title to the free-text index
 * (content + indexed metadata). Off for a typed query — a name search must NOT
 * surface files that merely mention the term. On for a tag-chip search, where
 * the term is a tag value SharePoint only indexes as free text.
 */
export function useKnowledgeSearch(
  token: string | null,
  query: string,
  includeContent = false,
): UseKnowledgeSearchResult {
  const { refreshGraphToken } = useAuthState();
  const [results, setResults] = useState<GraphSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = query.trim();

  useEffect(() => {
    if (!token || q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const handle = setTimeout(() => {
      searchDriveItems(token, q, refreshGraphToken, { includeContent })
        .then((hits) => {
          if (cancelled) return;
          setResults(hits);
          logConnectorSuccess("sharepoint", "search");
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError("Couldn't search the knowledge base. Try signing in again.");
          logConnectorFailure("sharepoint", e, "search");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [token, q, includeContent, refreshGraphToken]);

  return { results, loading, error };
}
